import { useEffect, useState } from 'react'
import DataGrid from '../data-grid/DataGrid'
import { useAppDispatch } from '../../reducers/store'
import { fetchAdmins, resendAdminInvite, selectSettings } from '../../reducers/settings'
import { useSelector } from 'react-redux'
import { adminColumns } from './AdminColumns'
import { Admin } from '../../types/SettingsType'
import toast from 'react-hot-toast'
import DeleteAdminModal from './DeleteAdminModal'
import { RoleType, SessionState } from '../../types/UserTypes'
import Popout from '../popout/Popout'
import { Button, SegmentedControl } from '@radix-ui/themes'
import { rolesOptions } from '../../strings/dropdown-options'
import { IconUserPlus } from '@tabler/icons-react'

function AdminSettings() {
  const { admins } = useSelector(selectSettings)
  const di = useAppDispatch()
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [showDeleteAdminModal, setShowDeleteAdminModal] = useState<boolean>(false)
  const [showEditPopout, setShowPopout] = useState<boolean>(false)
  const [popoutData, setPopoutData] = useState<Admin | undefined>(undefined)
  const [filteredData, setFilteredData] = useState<Admin[] | undefined>(undefined)
  const [inviteStatus, setInviteStatus] = useState<string>('ACTIVE')
  const currentSession = JSON.parse(localStorage.getItem('session') as string) as SessionState

  useEffect(() => {
    di(fetchAdmins())
  }, [])

  useEffect(() => {
    if (admins || (admins && (admins as Admin[]).length === 0)) {
      setFilteredData(admins.filter((admin) => admin.inviteAccepted === (inviteStatus === 'ACTIVE')))
      setDataLoading(false)
    } else if (!admins) {
      setDataLoading(true)
    }
  }, [admins, inviteStatus])

  const handleResendInvite = async (adminDetails: Admin) => {
    const sent = await di(resendAdminInvite(adminDetails))
    if (!sent) {
      showToast('An error occurred while resending the invite', '❌')
    } else {
      showToast(`An invite has been resent to ${adminDetails.name} at ${adminDetails.email}`, '✅')
    }
  }

  const handleDeleteAdmin = async (admin: Admin) => {
    if (admin.email === currentSession.user.email) {
      showToast('You cannot remove yourself as admin', '❌')
    } else {
      setShowDeleteAdminModal(true)
    }
  }

  const handleViewAdmin = (admin?: Admin) => {
    setShowPopout(true)
    setPopoutData(
      admin
        ? admin
        : { name: '', email: '', roles: rolesOptions.filter((role) => role.value === RoleType.GENERAL_ADMIN) }
    )
  }

  const showToast = (message: string, emoji: string) =>
    toast(message, {
      duration: 6000,
      icon: emoji,
      className: 'dark:bg-gray-600 dark:text-white',
    })

  const updateSection = (admin: Admin) => {
    admin.inviteAccepted || currentSession?.organization.ssoProvider
      ? setInviteStatus('ACTIVE')
      : setInviteStatus('INVITED')
  }

  const triggerEvent = (event?: string, admin?: Admin) => {
    switch (event) {
      case 'invite':
        handleResendInvite(admin!)
        break
      case 'delete':
        handleDeleteAdmin(admin!)
        break
      case 'update':
        updateSection(admin!)
        break
      default:
        break
    }
  }

  return (
    <>
      <div className="flex justify-between mb-3 mt-6">
        <SegmentedControl.Root
          defaultValue="ACTIVE"
          value={inviteStatus}
          className="max-w-[300px] min-h-[38px] bg-controllerBackground text-controllerText"
          onValueChange={(value) => setInviteStatus(value)}
        >
          <SegmentedControl.Item value="ACTIVE" className="px-8">
            Active
          </SegmentedControl.Item>
          <SegmentedControl.Item value="INVITED" className="px-8">
            Invited
          </SegmentedControl.Item>
        </SegmentedControl.Root>
        <Button
          className="bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple h-[38px] ml-4 cursor-pointer"
          onClick={() => {
            handleViewAdmin()
          }}
        >
          Invite Admin
        </Button>
      </div>
      <div className="w-full h-[calc(100vh_-_80px)] overflow-auto flex flex-col gap-4">
        {filteredData && filteredData.length === 0 ? (
          <div className="block items-center mt-20 flex flex-col gap-2">
            <div className="flex items-center justify-center">
              <IconUserPlus className="h-10 w-10" />
            </div>
            <p className="text-center text-lg font-semibold">No Invites Found</p>
            <p className="text-center text-sm">After you invite an admin, they will be shown here</p>
          </div>
        ) : (
          <DataGrid
            columns={adminColumns(handleViewAdmin)}
            data={filteredData}
            rowClass="!bg-tableRowGrey dark:bg-tableRowSecondary"
            headerClass="bg-tableHeaderGrey dark:bg-tableHeaderSecondary"
            loading={dataLoading}
          />
        )}
      </div>
      {showDeleteAdminModal && popoutData && (
        <DeleteAdminModal
          triggerEvent={setShowPopout}
          setShowModal={setShowDeleteAdminModal}
          deleteAdminEmail={popoutData.email}
        />
      )}
      {showEditPopout && (
        <Popout
          setShowPopout={setShowPopout}
          triggerEvent={triggerEvent}
          contentScreen="ADMIN"
          heightOffset={0}
          popoutData={popoutData}
        />
      )}
    </>
  )
}

export default AdminSettings
